import React, { useState, useEffect, useRef } from 'react';
import style from './BloodPressureWrite.module.css';
import HeadSub from '../../../../components/comm/HeadSub';
import Modal from '../../../../components/comm/Modal';
import CustomDatePicker from '../../../../components/datepicker/CustomDatePicker';
import CustomTimePicker from '../../../../components/datepicker/CustomTimePicker';
import BottomSheetBase from '../../../../components/comm/BottomSheetBase';
import URL from "../../../../constants/url";
import ToastPopup from '../../../../components/comm/ToastPopup';
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import axiosApi from '../../../../api/daonAxios';
import { getBloodPressureResultCode } from '../../../../utils/bp';

const BloodPressureWrite = () => {
    const navigate = useNavigate();

    const [recordDate, setRecordDate] = useState('');
    const [openRecordDate, setOpenRecordDate] = useState(false);
    const [recordTime, setRecordTime] = useState('');
    const [openRecordTime, setOpenRecordTime] = useState(false);
    const [time, setTime] = useState({
        hour: '',
        minute: '',
        second: ''
    });
    const systolicInputRef = useRef(null);
    const [toast, setToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [systolic, setSystolic] = useState('');
    const [diastolic, setDiastolic] = useState('');

    const isSendingRef = useRef(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTimeOpen, setModalTimeOpen] = useState(false);

    const focusDateInputRef = useRef(null);
    const focusTimeInputRef = useRef(null);
    const dayRef = useRef(null);
    const timeRef = useRef(null);

    const [dateInputFocused, setDateInputFocused] = useState(false);
    const [timeInputFocused, setTimeInputFocused] = useState(false);

    const handleDateConfirm = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const picked = new Date(date);
        picked.setHours(0, 0, 0, 0);

        if (picked > today) {
            setOpenRecordDate(false);
            setModalOpen(true);
            setDateInputFocused(true);
            return;
        }

        setRecordDate(picked);
        setOpenRecordDate(false);
        setDateInputFocused(false);

        setTimeout(() => {
            if (dayRef.current) {
                dayRef.current.focus({ preventScroll: true });
                dayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 200);
    };

    const closeModal = () => {
        setModalOpen(false);
        setTimeout(() => {
            focusDateInputRef.current?.focus({ preventScroll: true });
            focusDateInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
        setOpenRecordDate(true);
        setDateInputFocused(true);
    };

    const closeModalTime = () => {
        setModalTimeOpen(false);
        if (focusTimeInputRef.current) focusTimeInputRef.current.focus();
        setOpenRecordTime(true);
        setTimeInputFocused(true);
    };

    const handleConfirmTime = (value) => {
        const now = new Date();
        const selectedDateTime = new Date(recordDate || now);

        const hour = parseInt(value.hour, 10);
        const minute = parseInt(value.minute, 10);
        const second = Number(value.second ?? 0);

        selectedDateTime.setHours(hour, minute, second, 0);

        if (selectedDateTime > now) {
            setOpenRecordTime(false);
            setTimeout(() => setModalTimeOpen(true), 350);
            return;
        }

        
        setTime({
            hour: String(hour).padStart(2, '0'),
            minute: String(minute).padStart(2, '0'),
            second: String(second).padStart(2, '0')
        });

        const period = hour < 12 ? '오전' : '오후';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;

        setRecordTime(
            `${period} ${hour12}:${String(minute).padStart(2, '0')}`
        );

        setOpenRecordTime(false);
        setTimeInputFocused(false);

        setTimeout(() => {
            if (timeRef.current) {
                timeRef.current.focus({ preventScroll: true });
                timeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 200);
    };

    useEffect(() => {
        if (systolicInputRef.current) {
            setTimeout(() => {
                systolicInputRef.current.focus();
                systolicInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
        const now = new Date();
        setRecordDate(now);
        const hour = now.getHours();
        const minute = now.getMinutes();
        const second = now.getSeconds();
        
        setTime({
            hour: String(hour).padStart(2, '0'),
            minute: String(minute).padStart(2, '0'),
            second: String(second).padStart(2, '0')
        });

        const period = hour < 12 ? '오전' : '오후';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        setRecordTime(`${period} ${hour12}:${minute.toString().padStart(2, '0')}`);
    }, []);

    const handleComplete = async () => {
        if (!systolic) {
            setToastMessage('수축기 혈압을 입력해주세요.');
            setToast(true);
            return;
        }

        if (!diastolic) {
            setToastMessage('이완기 혈압을 입력해주세요.');
            setToast(true);
            return;
        }

        const brssrTop = Number(systolic);
        const brssrLwet = Number(diastolic);

        if (!Number.isFinite(brssrTop) || brssrTop < 0) {
            setToastMessage('수축기 혈압은 0 이상의 숫자로 입력해주세요.');
            setToast(true);
            return;
        }

        if (!Number.isFinite(brssrLwet) || brssrLwet < 0) {
            setToastMessage('이완기 혈압은 0 이상의 숫자로 입력해주세요.');
            setToast(true);
            return;
        }

        if (!recordDate) {
            setToastMessage('날짜를 선택해주세요.');
            setToast(true);
            return;
        }

        if (!time.hour || !time.minute) {
            setToastMessage('시간을 선택해주세요.');
            setToast(true);
            return;
        }

        if (isSendingRef.current) return;
        isSendingRef.current = true;

        const hour = String(time.hour).padStart(2, '0');
        const minute = String(time.minute).padStart(2, '0');
        const second = String(time.second ?? '00').padStart(2, '0');

        const saveObj = {
            recDe: dayjs(recordDate).format('YYYYMMDD'),
            recTm: `${hour}${minute}${second}`,
            brssrLwet,
            brssrTop,
            rsltCd: getBloodPressureResultCode(brssrTop, brssrLwet),
            cntcRecAt: '0',
        };

        try {
            console.log('혈압 저장 객체:', saveObj);

            await saveBp(saveObj);

            navigate(URL.BLOOD_PRESSURE_RECORD, {
                replace: true,
                state: {
                    showToast: true,
                    toastMessage: '기록 완료!',
                },
            });
        } catch (error) {
            console.error('혈압 저장 실패:', error);
            setToastMessage('혈압 기록을 저장하지 못했습니다.');
            setToast(true);
        } finally {
            isSendingRef.current = false;
        }
    };

    const handleDismissDate = () => {
        setOpenRecordDate(false);
        setDateInputFocused(false);
        setTimeout(() => {
            if (dayRef.current) {
                dayRef.current.focus({ preventScroll: true });
                dayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 200);
    };

    const handleDismissTime = () => {
        setOpenRecordTime(false);
        setTimeInputFocused(false);
        setTimeout(() => {
            if (timeRef.current) {
                timeRef.current.focus({ preventScroll: true });
                timeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 200);
    };

    const saveBp = async (saveObj) => {
        const jToken = localStorage.getItem('jToken');

        const requestOption = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        const response = await axiosApi.post('/bp', saveObj, requestOption);

        console.log('혈압 등록:', response.data.result);

    };

    return (
        <>
            <HeadSub title="혈압 기록하기" />

            <div id="sub-content" data-layout="white">
                <div className="inner-wrap">
                    <div className={style['record-writebox']}>
                        <div className={style['date-record']}>
                            <p>측정 일자</p>
                            <div className="input-box" data-type="c" ref={dayRef} tabIndex={0}>
                                <input type="button" inputMode="none" placeholder="25.02.07"
                                    value={recordDate ? dayjs(recordDate).format('YY.MM.DD') : ''}
                                    onClick={() => setOpenRecordDate(true)}
                                    className={dateInputFocused ? 'focused' : ''}
                                    ref={focusDateInputRef} readOnly />
                            </div>
                        </div>
                        <div className={style['time-record']}>
                            <p>측정 시간</p>
                            <div className="input-box" data-type="c" ref={timeRef} tabIndex={0}>
                                <input type="button" inputMode="none" placeholder="오후 01:30"
                                    value={recordTime}
                                    onClick={() => setOpenRecordTime(true)}
                                    className={timeInputFocused ? 'focused' : ''}
                                    ref={focusTimeInputRef} readOnly />
                            </div>
                        </div>
                        <div>
                            <p>수축기</p>
                            <div className="input-box" data-type="c">
                                <input placeholder="---" ref={systolicInputRef}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={systolic} 
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');

                                        if (value === '') {
                                            setSystolic('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (number <= 999) {
                                            setSystolic(value);
                                        }
                                    }}
                                    aria-label="수축기 값을 입력해주세요" />
                                <span className="unit">mmHg</span>
                            </div>
                        </div>
                        <div>
                            <p>이완기</p>
                            <div className="input-box" data-type="c">
                                <input placeholder="---"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={diastolic}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value === '') {
                                            setDiastolic('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (number <= 999) {
                                            setDiastolic(value);
                                        }
                                    }}
                                    aria-label="이완기 값을 입력해주세요" />
                                <span className="unit">mmHg</span>
                            </div>
                        </div>

                        {/* 날짜 바텀시트 */}
                        <BottomSheetBase open={openRecordDate} onDismiss={handleDismissDate}>
                            <CustomDatePicker
                                titleTxt="측정 일자 선택"
                                onClose={handleDismissDate}
                                onConfirm={handleDateConfirm}
                            />
                        </BottomSheetBase>

                        {/* 시간 바텀시트 */}
                        <BottomSheetBase open={openRecordTime} onDismiss={handleDismissTime}>
                            <CustomTimePicker
                                titleTxt="측정 시간 선택"
                                onClose={handleDismissTime}
                                onConfirm={handleConfirmTime}
                            />
                        </BottomSheetBase>
                    </div>

                    <div className="bottom-fixed-box">
                        <div className="inner-box">
                            <div className="btn-layout" data-btn-num="1">
                                <button className="btn" data-btn-type="fill-main" data-btn-size="lg"
                                    onClick={handleComplete}
                                    disabled={!systolic || !diastolic || !recordDate || !recordTime}>
                                    <span>완료</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {toast && <ToastPopup setToast={setToast} message={toastMessage} opacity="90" />}
                </div>
            </div>

            <Modal isOpen={modalOpen} setIsOpen={setModalOpen}>
                <div className="modal-box" data-type="a">
                    <div className="modal-top-box"><div className="title-box"><p>오늘 날짜보다 미래 날짜를 선택할 수 없습니다.</p></div></div>
                    <div className="modal-bottom-box"><div className="btn-layout"><button className="btn" data-btn-type="fill-gray" data-btn-size="md" onClick={closeModal}><span>확인</span></button></div></div>
                </div>
            </Modal>

            <Modal isOpen={modalTimeOpen} setIsOpen={setModalTimeOpen}>
                <div className="modal-box" data-type="a">
                    <div className="modal-top-box"><div className="title-box"><p>현재 시간보다 미래 시간을 선택할 수 없습니다.</p></div></div>
                    <div className="modal-bottom-box"><div className="btn-layout"><button className="btn" data-btn-type="fill-gray" data-btn-size="md" onClick={closeModalTime}><span>확인</span></button></div></div>
                </div>
            </Modal>
        </>
    );
};

export default BloodPressureWrite;